<script setup lang="ts">
  import ControlNumberInput from '@/components/control/ControlNumberInput.vue';
  import StoreMenuItemPicture from '@/components/shared/StoreMenuItemPicture.vue';
  import { useModal } from '@/composition/use-modal';
  import { useOrdering } from '@/composition/use-ordering';
  import { ICartItem } from '@/interfaces/ICart';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { displayPrice, roundPrice } from '@/utils';
  import { optionValuesToText } from '@/utils/orders.utils';
  import { inject, computed } from 'vue';
  import { useRouter } from 'vue-router/composables';
  import ModalPosNewOrderItem from './item/ModalPosNewOrderItem.vue';

  const props = defineProps<{
    item: ICartItem;
  }>();

  const restaurantStore = useRestaurantStore();
  const orderHelper = inject<ReturnType<typeof useOrdering>>('orderHelper');
  const router = useRouter();
  const modals = useModal();

  const quantity = computed({
    get: () => props.item.quantity,
    set: (value: number) => {
      if (value > 0) {
        props.item.quantity = value;
        props.item.totalPrice = roundPrice(props.item.unitPrice * value);
      } else {
        const itemIndex = orderHelper.cart.value.cartItems.findIndex(
          (i) => i.index === props.item.index
        );

        if (itemIndex !== -1) {
          orderHelper.cart.value.cartItems.splice(itemIndex, 1);
        }
      }
    },
  });

  const optionsSummary = computed(() => {
    let summary = optionValuesToText({
      item: props.item.item,
      optionValues: props.item.optionValues,
    });

    if (props.item.userComment) {
      if (summary) {
        summary += ', ';
      }
      summary += props.item.userComment;
    }

    return summary;
  });

  function edit() {
    modals.show(
      // @ts-ignore
      ModalPosNewOrderItem,
      {
        item: props.item.item,
        editingCartItem: props.item,
      },
      {
        name: 'pos-item',
        adaptive: true,
        classes: 'dialog--height-limit',
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 840,
        transition: 'none',
      },
      {
        save: async (item: ICartItem) => {
          const cartItem = orderHelper.cart.value.cartItems.find(
            (i) => i.index === item.index
          );

          Object.assign(cartItem, item);
        },
      }
    );
  }
</script>

<template>
  <div class="cart-item">
    <div class="picture-container">
      <StoreMenuItemPicture :item="item.item" size="md" class="picture" />
    </div>
    <div class="details">
      <span class="name">{{ item.item.name }}</span>
      <div v-if="optionsSummary" class="options-summary">
        {{ optionsSummary }}
      </div>
      <div class="price-container">
        <span class="price">
          {{ restaurantStore.currency?.symbol }}
          {{ displayPrice(item.totalPrice) }}
        </span>
      </div>
    </div>
    <div class="spacer"></div>
    <div class="actions">
      <v-btn icon x-small text color="grey" @click="edit">
        <v-icon>mdi-pencil</v-icon>
      </v-btn>
      <ControlNumberInput v-model="quantity" size="xs" />
    </div>
  </div>
</template>

<style scoped lang="scss">
  .cart-item {
    @apply tw-flex tw-items-start tw-space-x-2 tw-text-sm;
    @apply tw-px-2 tw-py-2;

    .picture-container {
      @apply tw-flex-none;
    }

    .details {
      @apply tw-flex tw-flex-col;

      .name {
      }

      .price-container {
        .price {
          @apply tw-font-semibold;
        }
      }

      .options-summary {
        @apply tw-text-gray-500 tw-text-xs;
      }
    }

    .actions {
      @apply tw-flex tw-items-center tw-flex-none tw-space-x-1;
    }
  }
</style>
